import { SteadoLoader } from '@/components/shell/SteadoLoader';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

export interface GymLinkPanelProps {
  readonly noun: 'food' | 'exercise';
  /** The catalog row just created. */
  readonly name: string;
  /** How many gym rows it was made from. */
  readonly count: number;
  readonly pending: boolean;
  /** Why the link failed, or null while it runs. */
  readonly error: string | null;
  readonly onRetry: () => void;
  readonly onOpen: () => void;
}

/**
 * In place of the create form once a catalog row made "From gyms" exists: the
 * gym rows are linked as a second call, and the form must not be offered
 * again, or a retry would create the row twice. A failed link stays here,
 * says so, and links again on request.
 */
export function GymLinkPanel({ noun, name, count, pending, error, onRetry, onOpen }: GymLinkPanelProps) {
  const rows = `${String(count)} gym ${noun}${count === 1 ? '' : 's'}`;
  if (error === null) {
    return (
      <p role="status" className="flex items-center gap-2 text-sm text-muted-foreground">
        <SteadoLoader />
        Linking the {rows} “{name}” was made from…
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      <Alert role="alert" tone="err">
        <AlertTitle>“{name}” was created, but the gyms’ rows were not linked</AlertTitle>
        <AlertDescription>
          The {rows} it was made from are still in the queue: {error}. Linking again skips any already linked.
        </AlertDescription>
      </Alert>
      <div className="flex flex-wrap gap-2">
        <Button type="button" onClick={onRetry} disabled={pending}>
          {pending ? 'Linking…' : 'Try linking again'}
        </Button>
        <Button type="button" variant="outline" onClick={onOpen} disabled={pending}>
          Open the new {noun}
        </Button>
      </div>
    </div>
  );
}
